import type { DomainProvider } from "@v2/features/settings/shop/constants/domainProviderOptions";

import * as React from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { toast } from "@replo/design-system/components/toast/Toast";
import twMerge from "@replo/design-system/utils/twMerge";
import { DOMAIN_PROVIDERS } from "@v2/features/settings/shop/constants/domainProviderOptions";
import { useSubscriptionInfo } from "@v2/hooks/subscription";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { docs } from "@v2/utils/docs";
import { CircleCheck, Clock, SquareArrowOutUpRight } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { isValidDomain } from "replo-utils/lib/url";

interface AddAnalyticsCustomDomainModalProps {
  projectId: string | null;
  workspaceId: string | null;
}

const DomainProviderButton = ({ name, url }: { name: string; url: string }) => (
  <Button
    variant="secondary"
    size="base"
    endEnhancer={<SquareArrowOutUpRight />}
    render={<a href={url} target="_blank" rel="noopener noreferrer" />}
  >
    {name}
  </Button>
);

const WaitingMessage = () => (
  <div className="bg-sky-soft rounded-lg p-4">
    <div className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1">
      <div className="flex items-center">
        <Clock size={16} />
      </div>
      <div className="text-default text-sm font-normal">
        Wait before checking connection
      </div>
      <div></div> {/* NOTE (Kurt, 2024-10-25): Empty cell for alignment */}
      <div className="text-placeholder text-sm font-normal">
        It can take up to five minutes for a provider to process the connection.
        Please wait a moment before checking.
      </div>
    </div>
  </div>
);

export const AddAnalyticsCustomDomainModal: React.FC<
  AddAnalyticsCustomDomainModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({ projectId, workspaceId, open, onOpenChange }) => {
  const { data: workspaceCustomDomains } =
    workspaceRouter.getV1CustomDomains.useQuery({
      projectId: projectId ?? undefined,
      workspaceId: workspaceId ?? undefined,
    });
  const [domainData, setDomainData] = React.useState({
    value: workspaceCustomDomains?.[0]?.value ?? "",
    isValid: isValidDomain(workspaceCustomDomains?.[0]?.value ?? ""),
  });

  // NOTE (Kurt, 2025-01-31): We're using the last domain in the array as the current domain.
  const domainDataEdited =
    workspaceCustomDomains?.[-1]?.value !== domainData.value;

  const {
    data: cnameResults,
    isLoading,
    isFetching,
    refetch,
  } = workspaceRouter.checkCustomDomain.useQuery(
    {
      projectId: projectId ?? undefined,
      workspaceId: workspaceId ?? undefined,
      customDomain: domainData.value,
    },
    // NOTE (Kurt, 2024-10-25): Disabled by default to prevent unnecessary queries. This flag
    // prevents the query from running until the user clicks the "Check Connection" button.
    {
      enabled: false,
    },
  );

  const status = (() => {
    if (isLoading || isFetching) {
      return "checking";
    }
    return cnameResults?.ok ? "ok" : "notOk";
  })();

  const isChecking = status === "checking";
  const [hasCheckedConnection, setHasCheckedConnection] = React.useState(false);

  const handleCheckConnection = () => {
    if (domainData.isValid) {
      setHasCheckedConnection(true);
      void refetch();
    }
  };

  const [error, setError] = React.useState<string | null>(null);

  const logEvent = useLogAnalytics();
  const { subscriptionInfo } = useSubscriptionInfo();
  const subscriptionTier = subscriptionInfo?.tier ?? "free";

  const { mutate: updateDomain, isPending: isUpdating } =
    workspaceRouter.createCustomDomain.useMutation({
      onSuccess: (response: { customDomain: string }) => {
        toast.success({
          title: "Custom Domain Updated",
          description: `${response.customDomain} is now available for use in experiments.`,
        });
        logEvent("experiment.domain.add", {
          billingPlanTier: subscriptionTier,
        });
        onOpenChange(false);
        void workspaceUtils.getV1CustomDomains.invalidate({
          projectId: projectId ?? undefined,
          workspaceId: workspaceId ?? undefined,
        });
      },
      meta: {
        // NOTE (Kurt, 2024-11-03): We'll display inline error instead of default toast handling
        reploIgnoreDefaultErrorHandling: true,
      },
    });

  const handleAddAndSave = () => {
    if ((workspaceId || projectId) && domainData.value) {
      setError(null);
      updateDomain(
        {
          projectId: projectId ?? undefined,
          workspaceId: workspaceId ?? undefined,
          customDomain: domainData.value,
        },
        {
          onError: (error) => {
            setError(
              error instanceof Error
                ? error.message
                : "Failed to add custom domain",
            );
          },
        },
      );
    }
  };

  const updateButtonDisabled =
    !domainDataEdited || status !== "ok" || isUpdating;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Use Custom Domain for Testing"
      size="base"
      footer={
        <div className="flex flex-row items-center gap-2">
          <Button
            variant="link"
            size="base"
            render={
              <a
                href={docs.abTesting.workspaceCustomDomains}
                target="_blank"
                rel="noopener noreferrer"
              />
            }
          >
            Learn More
          </Button>

          <Button
            variant="primary"
            size="base"
            disabled={updateButtonDisabled}
            onClick={handleAddAndSave}
            loading={isUpdating}
          >
            Add and Save
          </Button>
        </div>
      }
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-2 text-sm">
          <p>To use a custom domain instead of reploedge.com:</p>
          <p>
            Log in to your domain provider and add a CNAME record. Use your
            custom address (for example, go.my-store.com) as the host, and set
            the value to reploedge.com.
          </p>
          <div className="flex flex-col gap-2">
            <div className="header-h6">See guides from specific providers:</div>
            <div className="flex flex-wrap gap-2">
              {DOMAIN_PROVIDERS.map((provider: DomainProvider) => (
                <DomainProviderButton
                  key={provider.name}
                  name={provider.name}
                  url={provider.url}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="header-h6">Confirm your domain is connected</div>
          <WaitingMessage />
          <LabeledControl error={error ?? undefined}>
            <div className="flex flex-row gap-2">
              <Input
                size="base"
                type="text"
                placeholder="go.my-store.com"
                maxLength={256}
                onChange={(e) => {
                  setError(null);
                  setDomainData({
                    value: e.currentTarget.value,
                    isValid: isValidDomain(e.currentTarget.value ?? ""),
                  });
                }}
                value={domainData.value}
                invalid={Boolean(error)}
              />
              <Button
                variant="primary"
                size="base"
                onClick={handleCheckConnection}
                disabled={isChecking || !domainData.isValid}
              >
                Check Connection
              </Button>
            </div>
          </LabeledControl>
          {status !== "ok" ? (
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="flex-grow">
                {(isChecking || hasCheckedConnection) && domainData.value && (
                  <DomainCheckStatus
                    status={isChecking ? "checking" : status}
                    domain={domainData.value}
                    isValidDomain={domainData.isValid}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="bg-emerald-soft flex flex-row gap-6 rounded-lg p-4 py-2">
              <div className="flex flex-row items-center justify-start gap-4">
                <CircleCheck size={16} fill="var(--color-green-600)" />
                <div className="type-body-small text-emerald">
                  Domain connected
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

type DomainCheckStatusProps = {
  status: "ok" | "notOk" | "checking";
  domain: string;
  isValidDomain: boolean;
};

const DomainCheckStatus: React.FC<DomainCheckStatusProps> = ({
  status,
  isValidDomain,
}) => {
  return (
    <div className="flex w-full flex-row content-center items-center justify-start gap-x-2">
      <div
        className={twMerge(
          "group relative h-2.5 w-2.5 shrink-0 overflow-hidden rounded-full",
          status === "ok" && isValidDomain && "bg-emerald",
          (status === "notOk" || !isValidDomain) && "bg-rose",
          status === "checking" && "bg-surface",
        )}
      >
        {status === "checking" && (
          <div className="bg-default absolute h-4 w-4 animate-pulse opacity-0 transition-opacity duration-200 group-hover:opacity-100"></div>
        )}
      </div>
      <div className="shrink-0 text-sm font-normal">
        {exhaustiveSwitch({ type: status })({
          ok: () => "CNAME entry found",
          notOk: () => "No DNS entry found",
          checking: () => "Checking DNS entry",
        })}
      </div>
    </div>
  );
};
